import React, { useState, useMemo, useEffect } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type ColumnFiltersState,
  type ColumnResizeMode,
  type VisibilityState,
} from "@tanstack/react-table";
import { generateDynamicColumns } from "./ClaimsTable/generateColumns";
import { useHorizontalScrollbar } from "./ClaimsTable/useHorizontalScrollbar";
import { TableHeader } from "./ClaimsTable/TableHeader";
import { TableBody } from "./ClaimsTable/TableBody";
import { CustomScrollbar } from "./ClaimsTable/CustomScrollbar";
import { TablePagination } from "./ClaimsTable/TablePagination";
import { TABLE_STYLES } from "./ClaimsTable/tableStyles";

interface ClaimsTableProps {
  data: Record<string, any>[];
  isLoading?: boolean;
  globalFilter?: string;
  columnVisibility?: VisibilityState;
}

const COLUMN_VISIBILITY_KEY = "claimsTableColumnVisibility";

export const ClaimsTable: React.FC<ClaimsTableProps> = ({
  data,
  isLoading = false,
  globalFilter = "",
  columnVisibility: externalColumnVisibility,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnResizeMode] = useState<ColumnResizeMode>("onChange");
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});

  // Use external column visibility if provided, otherwise use internal
  const columnVisibility = externalColumnVisibility || internalColumnVisibility;
  const setColumnVisibility = externalColumnVisibility ? () => {} : setInternalColumnVisibility;

  // Generate columns dynamically based on data
  const columns = useMemo(() => generateDynamicColumns(data), [data]);

  // Load column visibility from localStorage on mount (only if not externally controlled)
  useEffect(() => {
    if (!externalColumnVisibility) {
      const savedVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
      if (savedVisibility) {
        try {
          setInternalColumnVisibility(JSON.parse(savedVisibility));
        } catch (e) {
          console.error("Failed to parse saved column visibility", e);
        }
      }
    }
  }, [externalColumnVisibility]);

  // Save column visibility to localStorage whenever it changes (only if not externally controlled)
  useEffect(() => {
    if (!externalColumnVisibility && Object.keys(internalColumnVisibility).length > 0) {
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(internalColumnVisibility));
    }
  }, [internalColumnVisibility, externalColumnVisibility]);

  const {
    scrollContainerRef,
    scrollThumbLeft,
    scrollThumbWidth,
    showScrollbar,
    handleThumbMouseDown,
  } = useHorizontalScrollbar(data);

  // Custom global filter function that only searches visible columns
  const customGlobalFilterFn = (row: any, columnId: string, filterValue: string) => {
    // Get all visible column IDs
    const visibleColumnIds = columns
      .map((col) => col?.id || (col as any)?.accessorKey)
      .filter((colId) => columnVisibility[colId] !== false);

    // If globalFilter is being applied, check only visible columns
    if (filterValue) {
      return visibleColumnIds.some((colId) => {
        const cellValue = row.getValue(colId);
        if (cellValue == null) return false;

        // Handle array values (like codesExplanation)
        if (Array.isArray(cellValue)) {
          return cellValue.some((item) => {
            if (typeof item === "object") {
              return Object.values(item).some((val) =>
                String(val).toLowerCase().includes(filterValue.toLowerCase())
              );
            }
            return String(item).toLowerCase().includes(filterValue.toLowerCase());
          });
        }

        return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
      });
    }
    return true;
  };

  const table = useReactTable({
    data,
    columns,
    columnResizeMode,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: customGlobalFilterFn,
    enableColumnResizing: true,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
    },
  });

  // Check if any column is being resized
  const isAnyColumnResizing = table.getState().columnSizingInfo.isResizingColumn !== false;

  return (
    <>
      <style>{TABLE_STYLES}</style>
      <div className=" shadow-sm overflow-visible">
        <div
          ref={scrollContainerRef}
          className="table-scroll-container min-h-24 max-w-full relative"
        >
          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-10 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="animate-spin rounded-none h-8 w-8 border-b-2 border-primary-blue"></div>
            </div>
          )}
          <table
            className={`w-full divide-y divide-border-gray ${isLoading ? "opacity-50" : ""} ${isAnyColumnResizing ? "table-no-select" : ""}`}
            style={{ width: "100%", minWidth: table.getCenterTotalSize(), tableLayout: "fixed" }}
          >
            <TableHeader headerGroups={table.getHeaderGroups()} />
            <TableBody rows={table.getRowModel().rows} />
          </table>
        </div>

        <CustomScrollbar
          show={showScrollbar}
          thumbLeft={scrollThumbLeft}
          thumbWidth={scrollThumbWidth}
          onThumbMouseDown={handleThumbMouseDown}
        />

        <TablePagination table={table} />
      </div>
    </>
  );
};

// Helper function to generate column headers from data
export const getColumnHeaders = (data: Record<string, any>[]): string[] => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];

  const allKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => allKeys.add(key));
  });

  return Array.from(allKeys)
    .filter((key) => key !== "claimId") // Exclude claimId
    .map((key) => {
      if (key === "codesExplanation") return "Codes Explanation";
      return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    });
};

// Helper to get column ID from header
export const getColumnIdFromHeader = (header: string, data: Record<string, any>[]): string => {
  if (header === "Codes Explanation") return "codesExplanation";

  if (!data || !Array.isArray(data) || data.length === 0) return header;

  const allKeys = Array.from(new Set(data.flatMap((item) => Object.keys(item))));

  const key = allKeys.find((k) => {
    const formattedKey = k
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
    return formattedKey === header;
  });

  return key || header;
};
